import { Box, Button, Card, Dialog, FormControl, IconButton, InputLabel, Link, MenuItem, Select, SelectChangeEvent, CircularProgress } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useEffect, useMemo, useState } from "react"
import PageHeader from "../../../components/PageHeader"
import useCustomerNumber from "../../../hooks/useCustomerNumber"
import { useAppDispatch, useAppSelector } from "../../../redux/store"
import { addError, addSuccess } from "../../../redux/alerts"
import { confirmAutoclave, getAutoclavesWithConfirmation, unassignBiobox, updateAutoclave, updateKitSterilization, getAutoclaveTypes, getAutoclaveFrequencies } from "../../../services/biologicalServices"
import { Autoclave, AutoclaveType, Frequency, TestKit } from "./types"
import { AutoclaveTypeId } from "./enums"
import { StyledDialogContent, StyledDialogFooter, StyledDialogTitle } from "../../../components/Dialog.styled"
import CloseModalButton from "../../../components/CloseModalButton"
import EditIcon from "@mui/icons-material/Edit"
import { getBackgroundComplianceColor } from "../../../helpers/compliance"
import { ComplianceStatus } from "../../../types"
import { useNavigate } from "react-router-dom"
import useIsCorpView from "../../../hooks/useIsCorpView"
import generateLink from "../../../helpers/generate-link"
import LoadingScreen from "../../../components/LoadingScreen"
import constants from "../../../constants"
import { getSingleChildPractice, selectCustomerNumber as selectCorpCustomerNumber } from "../../../redux/customer"
import { getAutoclaveFrequency, getAutoclaveTypeName } from "./utils/helpers"
import useCustomer from "../../../hooks/useCustomer"

export default function ConfirmAutoclaveType() {
  const customer = useCustomer()
  const metadata = customer.customerMetadata  
  
  const dispatch = useAppDispatch()
  const customerNumber = useCustomerNumber()
  const [loading, setLoading] = useState(false)
  const [autoclaves, setAutoclaves] = useState<Autoclave[]>([])
  const [autoclaveTypes, setAutoclaveTypes] = useState<AutoclaveType[]>([])
  const [autoclaveFrequencies, setAutoclaveFrequencies] = useState<Frequency[]>([])
  const [editTypeOpen, setEditTypeOpen] = useState(false)
  const [selectedAutoclave, setSelectedAutoclave] = useState<Autoclave | null>(null)
  const [selectedTypeId, setSelectedTypeId] = useState<number>(AutoclaveTypeId.Steam)
  const [savingType, setSavingType] = useState(false)
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false)
  const [autoclaveToDeactivate, setAutoclaveToDeactivate] = useState<Autoclave | null>(null)
  const showAutoclaveConfirmation =
    metadata != null &&
    metadata.customer_number === customerNumber &&
    (metadata.autoclave_outstanding ?? 0) > 0
  const corpCustomerNumber = useAppSelector(selectCorpCustomerNumber)
  const navigate = useNavigate()
  const isCorpView = useIsCorpView()

  const refreshSingleChildPractice = async () => {
    if (!isCorpView || !corpCustomerNumber || !customerNumber) return
    await dispatch(getSingleChildPractice({ customerNumber: corpCustomerNumber, childCustomerNumber: customerNumber }))
  }

  useEffect(() => {
    if (!customerNumber) return
    if (!metadata || metadata.customer_number !== customerNumber) return
    if (!showAutoclaveConfirmation) {
      navigate(generateLink(isCorpView, customerNumber, "dashboard"), { replace: true })
    }
  }, [customerNumber, metadata, showAutoclaveConfirmation, navigate, isCorpView])

  const loadAllData = async () => {
    if (!customerNumber) return
    setLoading(true)
    try {
      const autoclaveTypes = await getAutoclaveTypes(customerNumber)
      const autoclaveFrequencies = await getAutoclaveFrequencies(customerNumber)
      const autoclaveResults = await getAutoclavesWithConfirmation(customerNumber)

      setAutoclaveTypes(autoclaveTypes)
      setAutoclaveFrequencies(autoclaveFrequencies)
      setAutoclaves(autoclaveResults ?? [])
    } catch (err) {
      console.error(err)
      dispatch(addError("Failed to load biological data."))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAllData()
  }, [customerNumber])

  const handleDeactivate = async (autoclave: Autoclave) => {
    if (!customerNumber) return

    setLoading(true)

    try {
      await updateAutoclave(customerNumber, {
        customer_number: customerNumber,
        autoclave_id: autoclave.autoclave_id,
        autoclave_name: autoclave.autoclave_name,
        autoclavetype_id: autoclave.autoclavetype_id,
        autoclave_brand: autoclave.autoclave_brand,
        autoclave_model: autoclave.autoclave_model,
        autoclave_serial: autoclave.autoclave_serial,
        autoclave_frequency: autoclave.autoclave_frequency,
        active: false
      })

      await confirmAutoclave(customerNumber, autoclave, "deactivate")

      if (autoclave.bioboxes && autoclave.bioboxes.length > 0) {
        await Promise.all(autoclave.bioboxes.map(async (biobox) => {
          await unassignBiobox(customerNumber, Number(biobox.box_id))
        }))
      }

      await loadAllData()
      await refreshSingleChildPractice()
      dispatch(addSuccess("Autoclave deactivated."))
    } catch (err) {
      console.error(err)
      dispatch(addError("Failed to deactivate autoclave."))
    } finally {
      setLoading(false)
    }
  }

  const handleConfirm = async (autoclave: Autoclave) => {
    if (!customerNumber) return

    setLoading(true)

    try {
      await confirmAutoclave(customerNumber, autoclave, "confirm")

      await loadAllData()
      await refreshSingleChildPractice()
      dispatch(addSuccess("Autoclave type confirmed."))
    } catch (err) {
      console.error(err)
      dispatch(addError("Failed to confirm autoclave."))
    } finally {
      setLoading(false)
    }
  }

  const handleSaveType = async () => {
    if (!customerNumber || !selectedAutoclave) return

    setSavingType(true)

    try {
      await updateAutoclave(customerNumber, {
        customer_number: customerNumber,
        autoclave_id: selectedAutoclave.autoclave_id,
        autoclave_name: selectedAutoclave.autoclave_name,
        autoclavetype_id: selectedTypeId,
        autoclave_brand: selectedAutoclave.autoclave_brand,
        autoclave_model: selectedAutoclave.autoclave_model,
        autoclave_serial: selectedAutoclave.autoclave_serial,
        autoclave_frequency: selectedAutoclave.autoclave_frequency
      })

      await confirmAutoclave(customerNumber, { ...selectedAutoclave, autoclavetype_id: selectedTypeId }, "confirm")

      if (selectedAutoclave.bioboxes && selectedAutoclave.bioboxes.length > 0) {
        const autoclaveTypeAbbrById = new Map([
          [AutoclaveTypeId.Steam, "S"],
          [AutoclaveTypeId.DriHeat, "D"],
          [AutoclaveTypeId.Chemical, "C"],
          [AutoclaveTypeId.eTo, "D"],
        ])
        const abbreviation = autoclaveTypeAbbrById.get(Number(selectedTypeId ?? AutoclaveTypeId.Steam))

        await Promise.all(
          selectedAutoclave.bioboxes
            .filter((biobox) => biobox.tests_run < biobox.box_size)
            .map(async (biobox) => {
              if (abbreviation) {
                const testKit: TestKit = {
                  kitId: String(biobox.box_number),
                  sterilizationMethod: abbreviation,
                }
                await updateKitSterilization(customerNumber, testKit)
              }
            })
        )
      }

      dispatch(addSuccess("Autoclave type updated."))
      await loadAllData()
      await refreshSingleChildPractice()
      closeTypeModal()
    } catch (err) {
      console.error(err)
      const error = err as { response?: { data?: { message?: string } }; message?: string }
      dispatch(addError(error?.response?.data?.message || error?.message || "Failed to update autoclave type."))
    } finally {
      setSavingType(false)
    }
  }

  const openDeactivateConfirm = (autoclave: Autoclave) => {
    setAutoclaveToDeactivate(autoclave)
    setDeactivateConfirmOpen(true)
  }

  const closeDeactivateConfirm = () => {
    setDeactivateConfirmOpen(false)
    setAutoclaveToDeactivate(null)
  }

  const confirmDeactivate = async () => {
    if (!autoclaveToDeactivate) return
    await handleDeactivate(autoclaveToDeactivate)
    closeDeactivateConfirm()
  }

  const openTypeModal = (autoclave: Autoclave) => {
    setSelectedAutoclave(autoclave)
    setSelectedTypeId(Number(autoclave.autoclavetype_id ?? AutoclaveTypeId.Steam))
    setEditTypeOpen(true)
  }

  const closeTypeModal = () => {
    setEditTypeOpen(false)
    setSelectedAutoclave(null)
  }

  const rows = useMemo(
    () => autoclaves.map((autoclave) => ({ ...autoclave, id: autoclave.autoclave_id })).filter((autoclave) => autoclave.autoclave_confirmation != null),
    [autoclaves]
  )

  const autoclaveTypeById = useMemo(() => {
    const typeById = new Map<AutoclaveTypeId, AutoclaveType>(
      autoclaveTypes.map(type => ( [Number(type.autoclavetype_id), type] ))
    )

    return typeById
  }, [autoclaveTypes])

  const autoclaveFrequencyById = useMemo(() => {
    const frequencyById = new Map<number, Frequency>(
      autoclaveFrequencies.map(frequency => ( [frequency.key_, frequency] ))
    )

    return frequencyById
  }, [autoclaveFrequencies])  

  const getConfirmedBy = (row: any) => {
    const updatedByUser = row?.autoclave_confirmation?.updated_by_user
    const firstName = updatedByUser?.firstname?.trim?.() ?? ""
    const lastName = updatedByUser?.lastname?.trim?.() ?? ""
    const email = updatedByUser?.email?.trim?.() ?? ""
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim()
    return fullName || email || "-"
  }

  const columns: GridColDef[] = [
    { field: "autoclave_name", headerName: "Autoclave Name", flex: 1, minWidth: 180 },
    { field: "autoclave_serial", headerName: "Serial/Sterilizer #", flex: 1, minWidth: 180 },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => getAutoclaveTypeName(row, autoclaveTypeById) || "-",
      renderCell: (params) => {
        const autoclaveTypeName = getAutoclaveTypeName(params.row, autoclaveTypeById)

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
            <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {autoclaveTypeName || "-"}
            </Box>
            {!params.row?.autoclave_confirmation?.updated_date && (
              <IconButton
                size="small"
                aria-label="edit autoclave type"
                onClick={() => openTypeModal(params.row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        )
      },
    },
    { field: "autoclave_brand", headerName: "Brand", flex: 1, minWidth: 140 },
    { field: "autoclave_model", headerName: "Model", flex: 1, minWidth: 140 },
    {
      field: "frequency",
      headerName: "Frequency",
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => getAutoclaveFrequency(row, autoclaveFrequencyById) || "-",
    },
    {
      field: "autoclave_confirmation.updated_date",
      headerName: "Confirmation Date",
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => row.autoclave_confirmation?.updated_date || "-",
      renderCell: (params) => {
        const updatedDate = params.row.autoclave_confirmation?.updated_date
        return (
          <Box sx={{ width: "100%", textAlign: updatedDate ? "left" : "center" }}>
            {updatedDate ? new Date(updatedDate).toLocaleDateString() : "-"}
          </Box>
        )
      },
    },
    {
      field: "updated_by_user",
      headerName: "Confirmed By",
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => getConfirmedBy(row),
      renderCell: (params) => {
        const hasConfirmedBy = Boolean(params.row.autoclave_confirmation?.updated_by_user)
        return (
          <Box sx={{ width: "100%", textAlign: hasConfirmedBy ? "left" : "center" }}>
            {hasConfirmedBy ? getConfirmedBy(params.row) : "-"}
          </Box>
        )
      },
    },
    {
      field: "deactivate",
      headerName: "",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          disabled={!params.row.active || Boolean(params.row?.autoclave_confirmation?.updated_date)}
          sx={{
            backgroundColor: "grey.500",
            color: "common.white",
            "&:hover": {
              backgroundColor: "grey.700",
            },
          }}
          onClick={() => openDeactivateConfirm(params.row)}
        >
          Deactivate
        </Button>
      ),
    },
    {
      field: "confirm",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          disabled={Boolean(params.row?.autoclave_confirmation?.updated_date)}
          onClick={() => handleConfirm(params.row)}
        >
          Confirm
        </Button>
      ),
    },
  ]

  const RecallLink = () => {
    return (
      <Link
        sx={{ ml: 1, fontSize: "16px" }}
        href={constants.MESA_RECALL_2026_PDF_URL}
        target="_blank"
        rel="noopener noreferrer"
        download
      >
        Why are we asked to do this?
      </Link>
    )
  }

  return (
    (!metadata || metadata.customer_number !== customerNumber) ? (
      <LoadingScreen />
    ) : (
      <Box>
        <PageHeader iconName="square-check" headerName={<>Confirm Autoclave Type <RecallLink /></>} size="26px" />
        <Card variant="outlined" sx={{ m: 1.5 }}>
          <div className="box-header b-b">Autoclaves</div>
          <DataGrid
            sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderTop: "none", borderRadius: "0" }}
            density="compact"
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            autoHeight
            loading={loading}
            pageSizeOptions={[10, 20, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
          />
        </Card>
        <Dialog open={editTypeOpen} onClose={closeTypeModal} fullWidth maxWidth="sm">
          <CloseModalButton handleClose={closeTypeModal} />
          <StyledDialogTitle>Edit Autoclave Type</StyledDialogTitle>
          <StyledDialogContent>
            <FormControl fullWidth>
              <InputLabel id="autoclave-type-label">Type</InputLabel>
              <Select<number>
                size="small"
                labelId="autoclave-type-label"
                label="Type"
                value={selectedTypeId}
                onChange={(event: SelectChangeEvent<number>) => setSelectedTypeId(Number(event.target.value))}
              >
                <MenuItem value={AutoclaveTypeId.Steam}>Steam</MenuItem>
                <MenuItem value={AutoclaveTypeId.DriHeat}>Dri-heat</MenuItem>
                <MenuItem value={AutoclaveTypeId.Chemical}>Chemical</MenuItem>
                <MenuItem value={AutoclaveTypeId.eTo}>eTo</MenuItem>
              </Select>
            </FormControl>
          </StyledDialogContent>
          <StyledDialogFooter>
            <Button onClick={closeTypeModal} disabled={savingType}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveType} disabled={savingType}>
              {savingType ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </StyledDialogFooter>
        </Dialog>
        <Dialog open={deactivateConfirmOpen} onClose={closeDeactivateConfirm} fullWidth maxWidth="sm">
          <CloseModalButton handleClose={closeDeactivateConfirm} />
          <StyledDialogTitle>
            <Box className="flex flex-align-center c-g-8">
              <span className={`m-r-8 compliance-circle compliance-circle-large ${getBackgroundComplianceColor(ComplianceStatus.Expired)}`} />
              <span>
                Deactivate Autoclave
              </span>
            </Box>
          </StyledDialogTitle>
          <StyledDialogContent>
            <Box sx={{ textAlign: "center" }}>
              Are you sure you want to Deactivate Autoclave {autoclaveToDeactivate?.autoclave_name}?
            </Box>
          </StyledDialogContent>
          <StyledDialogFooter>
            <Button disabled={loading} variant="outlined" onClick={closeDeactivateConfirm}>No</Button>
            <Button disabled={loading} variant="contained" onClick={confirmDeactivate}>
              {loading ? <CircularProgress size={20} /> : "Yes"}
            </Button>
          </StyledDialogFooter>
        </Dialog>
      </Box>
    )
  )
}
